export type AdminPermissionKey =
  | 'dashboard'
  | 'clients'
  | 'projects'
  | 'sites'
  | 'planning'
  | 'placements'
  | 'monitoring'
  | 'orders'
  | 'archive'
  | 'publishers'
  | 'pending_sites'
  | 'price_change'
  | 'users'
  | 'audit'
  | 'settings'
  | 'invoices'
  | 'admin_billing'
  | 'reports'
  | 'data_entry'
  | 'pricing_sites'
  | 'historical_usage'
  | 'competitor_requests'
  | 'sites.add'
  | 'sites.edit'
  | 'sites.archive'
  | 'sites.export'
  | 'sites.bulk_upload'
  | 'sites.check_all'
  | 'sites.filter.publisher'
  | 'sites.filter.outreach'
  | 'sites.filter.status'
  | 'sites.filter.date_added'
  | 'sites.filter.client_pricing'
  | 'sites.column.base_prices'
  | 'sites.column.outreach_email'
  | 'sites.price_override'
  | 'monitoring.import_urls'
  | 'placements.import_orders';

export interface PermissionDefinition {
  key: AdminPermissionKey;
  label: string;
  description?: string;
  routePrefix?: string;
}

export interface PermissionGroup {
  id: string;
  title: string;
  description?: string;
  permissions: PermissionDefinition[];
}

export const ROUTE_PERMISSIONS: PermissionDefinition[] = [
  { key: 'dashboard', label: 'Home', routePrefix: '/dashboard' },
  { key: 'clients', label: 'Clients', routePrefix: '/clients' },
  { key: 'projects', label: 'Projects', routePrefix: '/projects' },
  { key: 'sites', label: 'Sites', routePrefix: '/sites' },
  { key: 'planning', label: 'Planning', routePrefix: '/planning' },
  { key: 'placements', label: 'Orders', routePrefix: '/guest-blog-placements' },
  { key: 'monitoring', label: 'Backlink Health', routePrefix: '/live-link-status' },
  { key: 'orders', label: 'Transactions', routePrefix: '/orders' },
  { key: 'archive', label: 'Archive', routePrefix: '/archive' },
  { key: 'publishers', label: 'Publishers', routePrefix: '/publishers' },
  { key: 'pending_sites', label: 'Site Verifications', routePrefix: '/pending-sites' },
  { key: 'price_change', label: 'Cost Update', routePrefix: '/price-change-requests' },
  { key: 'users', label: 'Users', routePrefix: '/users' },
  { key: 'audit', label: 'Logs', routePrefix: '/audit' },
  { key: 'settings', label: 'Settings', routePrefix: '/settings' },
  { key: 'invoices', label: 'Client Billing', routePrefix: '/invoices' },
  { key: 'admin_billing', label: 'Publisher Billing', routePrefix: '/admin-billing' },
  { key: 'reports', label: 'Reports', routePrefix: '/reports' },
  { key: 'data_entry', label: 'Data Entry', routePrefix: '/data-entry' },
  { key: 'pricing_sites', label: 'Pricing Sites', routePrefix: '/pricing-sites' },
  { key: 'historical_usage', label: 'Historical Usage', routePrefix: '/projects/:id/historical-usage' },
  { key: 'competitor_requests', label: 'Competitor Requests', routePrefix: '/competitor-backlink-site-requests' },
];

export const PLACEMENTS_FEATURE_PERMISSIONS: PermissionDefinition[] = [
  { key: 'placements.import_orders', label: 'Import Orders', description: 'Import guest blog orders from CSV or Excel' },
];

export const MONITORING_FEATURE_PERMISSIONS: PermissionDefinition[] = [
  { key: 'monitoring.import_urls', label: 'Import URLs', description: 'Import external URLs from CSV on Backlink Health' },
];

export const SITES_FEATURE_PERMISSIONS: PermissionDefinition[] = [
  { key: 'sites.add', label: 'Add Site', description: 'Add new guest blog sites' },
  { key: 'sites.edit', label: 'Edit Site', description: 'Edit existing site details' },
  { key: 'sites.archive', label: 'Archive Site', description: 'Archive inactive sites' },
  { key: 'sites.export', label: 'Export CSV', description: 'Export filtered site list' },
  { key: 'sites.bulk_upload', label: 'Bulk Upload', description: 'Bulk upload sites from spreadsheet' },
  { key: 'sites.check_all', label: 'Check All Sites', description: 'Run health check on all sites' },
  { key: 'sites.filter.publisher', label: 'Publisher Filter', description: 'Filter sites by publisher' },
  { key: 'sites.filter.outreach', label: 'Outreach Email Filter', description: 'Filter sites by outreach email' },
  { key: 'sites.filter.status', label: 'Status Filter', description: 'Filter active/inactive sites' },
  { key: 'sites.filter.date_added', label: 'Date Added Filter', description: 'Filter by site added date range' },
  { key: 'sites.filter.client_pricing', label: 'Client Pricing Selectors', description: 'Client and project pricing dropdowns' },
  { key: 'sites.column.base_prices', label: 'Base Price Columns', description: 'GB/LI base price columns' },
  { key: 'sites.column.outreach_email', label: 'Outreach Email Column', description: 'Outreach email column in table' },
  { key: 'sites.price_override', label: 'Price Overrides', description: 'Client-specific price override actions' },
];

export const ALL_ADMIN_PERMISSION_KEYS: AdminPermissionKey[] = [
  ...ROUTE_PERMISSIONS.map((p) => p.key),
  ...SITES_FEATURE_PERMISSIONS.map((p) => p.key),
  ...PLACEMENTS_FEATURE_PERMISSIONS.map((p) => p.key),
  ...MONITORING_FEATURE_PERMISSIONS.map((p) => p.key),
];

const DEFAULT_ADMIN_PRESET: AdminPermissionKey[] = [
  'dashboard',
  'clients',
  'projects',
  'sites',
  'planning',
  'placements',
  'monitoring',
  'orders',
  'archive',
  'sites.filter.client_pricing',
];

export function getDefaultAdminPermissions(): AdminPermissionKey[] {
  return [...DEFAULT_ADMIN_PRESET];
}

export function isValidPermission(key: string): key is AdminPermissionKey {
  return (ALL_ADMIN_PERMISSION_KEYS as string[]).includes(key);
}

export function sanitizeAdminPermissions(input: unknown): AdminPermissionKey[] {
  if (!Array.isArray(input)) return getDefaultAdminPermissions();
  const unique = Array.from(new Set(input.filter((k): k is string => typeof k === 'string')));
  const valid = unique.filter(isValidPermission);
  return valid.length > 0 ? valid : getDefaultAdminPermissions();
}

export function groupPermissionsForUI(): PermissionGroup[] {
  return [
    {
      id: 'menu',
      title: 'Menu Access',
      description: 'Which pages and menu items this admin can open.',
      permissions: ROUTE_PERMISSIONS,
    },
    {
      id: 'sites',
      title: 'Sites Page',
      description: 'Filters, actions, and columns on the Guest Blog Sites page.',
      permissions: SITES_FEATURE_PERMISSIONS,
    },
    {
      id: 'placements',
      title: 'Guest Blog Orders',
      description: 'Actions on the Guest Blog Orders page.',
      permissions: PLACEMENTS_FEATURE_PERMISSIONS,
    },
    {
      id: 'monitoring',
      title: 'Backlink Health',
      description: 'Actions on the Backlink Health (live link monitoring) page.',
      permissions: MONITORING_FEATURE_PERMISSIONS,
    },
  ];
}

export const RAIL_ITEM_PERMISSION_MAP: Record<string, AdminPermissionKey> = {
  home: 'dashboard',
  clients: 'clients',
  projects: 'projects',
  sites: 'sites',
  planning: 'planning',
  placements: 'placements',
  monitoring: 'monitoring',
  publishers: 'publishers',
  pending: 'pending_sites',
  prices: 'price_change',
  orders: 'orders',
  users: 'users',
  audit: 'audit',
  settings: 'settings',
  archive: 'archive',
};
